import React, { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useHistory } from 'react-router-dom'

import { Col, Row } from 'antd'
import { Formik } from 'formik'
import * as Yup from 'yup'

import deletePremiumExpense from 'core/application/expenses/deleteInsurancePremium/DeletePremium'
import { getInsurancePremiumDetails } from 'core/application/expenses/getInsurancePremiumDetails'
import { InsurancePremiumResponse, RecurringExpenseResponse } from 'core/application/expenses/getInsurancePremiums'
import { ExpenseStatus } from 'core/domain/expenses/expenseStatus'

import { commandRequest } from '../../../../common/RequestUtils'
import { DefaultSelect } from '../../../../common/components'
import Card from '../../../../common/components/dataDisplay/Card'
import Popover from '../../../../common/components/dataDisplay/Popover'
import Tag from '../../../../common/components/dataDisplay/Tag'
import Modal from '../../../../common/components/feedback/Modal'
import notification from '../../../../common/components/feedback/Notification'
import Button from '../../../../common/components/general/Button'
import Icon from '../../../../common/components/general/Icon'
import { formatMonthAndDateOnly } from '../../../../common/dateFormatters'
import useQueryRequest from '../../../../common/hooks/useQueryRequest'
import { formatMoney } from '../../../../common/moneyFormatter'
import { ROUTE_PATHS } from '../../../../common/routePaths'
import ChangeEndDateModal from './ChangeEndDateModal'
import EditInsurancePremiumModal from './EditInsurancePremiumModal'
import InsurancePremiumModal from './InsurancePremiumModal'

import './insurancePremiumStyles.less'

type InsurancePremiumCardProps = {
  insurancePremium: InsurancePremiumResponse
  planPeriodId: string
  onPremiumUpdated?: (premiumId: string) => void
}

const InsurancePremiumCard: React.FC<InsurancePremiumCardProps> = ({
  insurancePremium,
  planPeriodId,
  onPremiumUpdated,
}) => {
  const history = useHistory()

  const [openModal, setOpenModal] = useState(false)
  const [editEndDateModal, setEditEndDateModal] = useState(false)
  const { t: translate } = useTranslation()

  const [deleteInsurancePremiumModal, setDeleteInsurancePremiumModal] = useState(false)
  const [insurancePremiumDetails, , sendGetInsurancePremiumDetails] = useQueryRequest(null)
  const [selectedRecurringExpense, setSelectedRecurringExpense] = useState<RecurringExpenseResponse | null>(null)

  const handleModalOpen = async () => {
    await sendGetInsurancePremiumDetails(() => getInsurancePremiumDetails(planPeriodId, selectedRecurringExpense.id))
    setOpenModal(true)
  }

  const handleModalClose = () => {
    setOpenModal(false)
  }

  const handleEditEndDateModalOpen = async () => {
    if (
      selectedRecurringExpense.expenseStatus === ExpenseStatus.AwaitingReview ||
      selectedRecurringExpense.expenseStatus === ExpenseStatus.ActionRequired
    ) {
      history.push(
        `${ROUTE_PATHS.ELIGIBLE_EMPLOYEE_EDIT_PREMIUM_EXPENSE}/?expenseTypeId=${selectedRecurringExpense.expenseType.id}&planPeriodId=${planPeriodId}&recurringExpenseId=${selectedRecurringExpense.id}&premiumId=${insurancePremium.id}`,
      )
    } else {
      setEditEndDateModal(true)
    }
  }
  const handleEditEndDateModalClose = () => {
    setEditEndDateModal(false)
  }
  const [openEndDateModal, setOpenEndDateModal] = useState(false)

  const handleEndDateChange = () => {
    setOpenEndDateModal(true)
    setEditEndDateModal(false)
  }

  const handleDeleteInsurancePremium = (insurancePremiumID) => {
    setDeleteInsurancePremiumModal(true)
    Modal.confirm({
      title: 'Delete reimbursement request?',
      content: "If you remove this request you will lose the information you've provided.",
      async onOk() {
        const deleteInsurancePremium = await commandRequest(() => deletePremiumExpense(insurancePremiumID))
        if (deleteInsurancePremium.errors) {
          notification.open({
            message: deleteInsurancePremium.errors[0],
            type: 'error',
          })
        } else {
          notification.open({
            type: 'success',
            message: 'Premium request deleted.',
          })
          window.location.reload()
        }
      },
      onCancel() {
        setDeleteInsurancePremiumModal(false)
      },
      okText: 'Yes, delete',
      okType: 'primary',
      width: 500,
      okButtonProps: { danger: true },
      cancelText: 'No, cancel',
    })
  }

  useEffect(() => {
    setSelectedRecurringExpense(
      insurancePremium.recurringExpenses.length > 0 ? insurancePremium.recurringExpenses[0] : null,
    )
  }, [insurancePremium])

  return (
    <>
      {selectedRecurringExpense && (
        <div className={'insurance-premium-col'}>
          <Card
            title={insurancePremium?.name}
            className="card ant-card-body mb-16 t-premium-card"
            extra={
              <>
                {selectedRecurringExpense.expenseStatus === ExpenseStatus.AwaitingReview && (
                  <Icon
                    className="far fa-trash-alt mr-16 t-delete"
                    onClick={() => handleDeleteInsurancePremium(selectedRecurringExpense.id)}
                  />
                )}
                <Icon className="fas fa-pencil t-edit" onClick={handleEditEndDateModalOpen} />{' '}
              </>
            }
            actions={[
              <Button
                type="link"
                iconPosition="right"
                icon={'fal fa-arrow-right t-full-details'}
                onClick={handleModalOpen}
              >
                <u>Full details</u>
              </Button>,
            ]}
          >
            <div className="background-gray">
              <Row justify={'space-between'}>
                <Col>
                  <h6>
                    <Icon className="fa fa-calendar" /> Valid through
                  </h6>
                </Col>

                <Col style={{ minWidth: '200px;' }}>
                  <Row justify="end">
                    {insurancePremium?.recurringExpenses.length == 1 && (
                      <h6 className={'t-date'}>
                        {formatMonthAndDateOnly(selectedRecurringExpense.startDate)} -{' '}
                        {formatMonthAndDateOnly(selectedRecurringExpense.endDate)}
                      </h6>
                    )}

                    {insurancePremium?.recurringExpenses.length > 1 && (
                      <Formik
                        enableReinitialize={true}
                        validationSchema={Yup.object({})}
                        initialValues={{}}
                        onSubmit={() => {}}
                        validate={(values) => {}}
                      >
                        <DefaultSelect
                          className={'w-100 t-select-date'}
                          name={`r_${insurancePremium.id}`}
                          value={[selectedRecurringExpense.id]}
                          onChange={(value) => {
                            setSelectedRecurringExpense(insurancePremium.recurringExpenses.find((x) => x.id === value))
                          }}
                          items={insurancePremium?.recurringExpenses.map((x) => {
                            return {
                              id: x.id,
                              name: `${formatMonthAndDateOnly(x.startDate)} - ${formatMonthAndDateOnly(x.endDate)}`,
                            }
                          })}
                        />
                      </Formik>
                    )}
                  </Row>
                </Col>
              </Row>
            </div>
            <Row className="p-16">
              <Col span={24}>
                <Row justify={'space-between'}>
                  <Col>
                    <h6>
                      <Icon className="fa fa-money-check-alt mr-8" />
                      Amount per month
                    </h6>
                    <h4 className="card-amount mb-4 t-amount">
                      {formatMoney(selectedRecurringExpense.requestedAmount)}
                    </h4>
                  </Col>
                  <Col>
                    <Row justify="end">
                      <ExpenseStatusLabel
                        expenseStatus={selectedRecurringExpense.expenseStatus}
                        messageToEmployee={selectedRecurringExpense.messageToEmployee}
                        key={selectedRecurringExpense.id}
                      />
                    </Row>
                  </Col>
                </Row>
              </Col>
            </Row>
          </Card>
        </div>
      )}

      {openModal && insurancePremiumDetails && selectedRecurringExpense && (
        <InsurancePremiumModal
          insurancePremiumDetails={insurancePremiumDetails}
          onCancel={handleModalClose}
          onOk={handleModalClose}
          status={selectedRecurringExpense.expenseStatus}
          startDate={selectedRecurringExpense.startDate}
          endDate={selectedRecurringExpense.endDate}
          title={insurancePremium?.name}
        />
      )}

      {editEndDateModal && selectedRecurringExpense && (
        <EditInsurancePremiumModal
          premiumId={selectedRecurringExpense.id}
          recurringExpenseId={selectedRecurringExpense.id}
          expenseTypeId={selectedRecurringExpense.expenseType.id}
          planPeriodId={planPeriodId}
          insurancePremiumDetails={insurancePremiumDetails}
          onCancel={handleEditEndDateModalClose}
          visible={editEndDateModal}
          onOk={handleEditEndDateModalClose}
          handleEndDateChange={handleEndDateChange}
        />
      )}
      {openEndDateModal && selectedRecurringExpense && (
        <ChangeEndDateModal
          recurringExpenseStartDate={selectedRecurringExpense.startDate}
          premiumId={selectedRecurringExpense.id}
          planPeriodId={planPeriodId}
          expenseTypeId={selectedRecurringExpense.expenseType.id}
          onCancel={() => setOpenEndDateModal(false)}
          onPremiumUpdated={onPremiumUpdated}
        />
      )}
    </>
  )
}

type ExpenseStatusLabelProps = {
  expenseStatus: ExpenseStatus
  messageToEmployee?: string
}

const ExpenseStatusLabel = ({ expenseStatus, messageToEmployee }: ExpenseStatusLabelProps) => {
  switch (expenseStatus) {
    case ExpenseStatus.Verified:
      return (
        <Tag
          key={expenseStatus}
          icon={<Icon className={`fal fa-check-circle mr-4 t-${expenseStatus}`} />}
          color="success"
        >
          Approved
        </Tag>
      )
    case ExpenseStatus.Ineligible:
      return (
        <Tag
          key={expenseStatus}
          icon={<Icon className={`fal fa-times-circle mr-4 t-${expenseStatus}`} />}
          color="error"
        >
          <Popover
            trigger="hover"
            content={<div className="font-12" dangerouslySetInnerHTML={{ __html: messageToEmployee }} />}
            placement="top"
          >
            Rejected
          </Popover>
        </Tag>
      )
    //
    case ExpenseStatus.ActionRequired:
      return (
        <Tag
          key={expenseStatus}
          icon={<Icon className={`fal fa-exclamation-circle mr-4 t-${expenseStatus}`} />}
          color="warning"
        >
          <Popover
            trigger="hover"
            content={<div className="font-12" dangerouslySetInnerHTML={{ __html: messageToEmployee }} />}
            placement="top"
          >
            Action Required
          </Popover>
        </Tag>
      )
    default:
      return (
        <Tag key={expenseStatus} icon={<Icon className={`fal fa-clock mr-4 t-${expenseStatus}`} />} color="processing">
          Awaiting Review
        </Tag>
      )
  }
}
export default InsurancePremiumCard
